import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';

import styles from './LatestResults.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import { Form, Formik } from 'formik';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import Input from '@src/components/_shared/UI/Input';
import { revenueSchema } from '@src/utilities/schemas';
import { useMutation } from '@tanstack/react-query';
import { companyUpdate } from '@src/api/Registration';
import { formatNumberWithSpaces, handleApiError } from '@src/utilities/functions';

export default function LatestResults() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();

    const { basic, results } = registration.company;
    const { mutate, isPending } = useMutation({
        mutationFn: () => companyUpdate({ value: { basic, results }, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id }),
        onSuccess: () => {
            setBlock('Informations')
        },
        onError: (error: any) => handleApiError(error)
    });

    const _onGoBack = () => {
        setSection(4)
        setBlock('LCBFT')
    };

    return (
        <Container>
            <BackButton onClick={_onGoBack}/>
            <BlockTitle title="Résultats de la société des deux dernières années" />
            <Subtitle content="Veuillez compléter tous les champs" />

            <Formik
                initialValues={{
                    current: {
                        revenue: registration.company.results.current.revenue?.toString() || '',
                        capital: registration.company.results.current.capital?.toString() || '',
                        equity: registration.company.results.current.equity?.toString() || '',
                        assets: registration.company.results.current.assets?.toString() || '',
                        cash: registration.company.results.current.cash?.toString() || ''
                    },
                    previous: {
                        revenue: registration.company.results.previous.revenue?.toString() || '',
                        capital: registration.company.results.previous.capital?.toString() || '',
                        equity: registration.company.results.previous.equity?.toString() || '',
                        assets: registration.company.results.previous.assets?.toString() || '',
                        cash: registration.company.results.previous.cash?.toString() || ''
                    }
                }}
                onSubmit={(values) => {
                    const formatValues = (obj: Record<string, string>) => {
                        return Object.fromEntries(
                            Object.entries(obj).map(([key, value]) => [
                                key, 
                                value ? value.toString().replace(',', '.') : ''
                            ])
                        );
                    };
                    const formattedValues = {
                        current: formatValues(values.current),
                        previous: formatValues(values.previous)
                    };
                    setField('company.results', formattedValues);
                    mutate();
                }}
                validationSchema={revenueSchema}
            >
                {({ handleChange, handleSubmit, values, errors, isValid, handleBlur, touched }) => {
                    const handleNumericChange = (fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
                        const numericValue = e.target.value.replace(/\s/g, '');
                        if (!isNaN(Number(numericValue))) {
                            handleChange(fieldName)(numericValue);
                        }
                    };
                    return (
                        <Form id="revenueForm" autoComplete="off" className="registerForm">
                            {/* Chiffre d’affaires */}
                            <div className={styles.inputGroup}>
                                <Input
                                    title="Chiffre d’affaires : N"
                                    name="current.revenue"
                                    id="current.revenue"
                                    width="100%"
                                    type="number"
                                    placeholder="Ex : 0"
                                    value={formatNumberWithSpaces(values.current.revenue)}
                                    onChange={handleNumericChange('current.revenue')}
                                    clearInput={() => handleChange('current.revenue')('')}
                                    hasError={(touched.current?.revenue && !!errors.current?.revenue) as boolean}
                                    onBlur={handleBlur('current.revenue')}
                                />
                                <Input
                                    title="Chiffre d’affaires : N-1"
                                    name="previous.revenue"
                                    id="previous.revenue"
                                    width="100%"
                                    type="number"
                                    placeholder="Ex : 0"
                                    value={formatNumberWithSpaces(values.previous.revenue)}
                                    onChange={handleNumericChange('previous.revenue')}
                                    clearInput={() =>handleChange('previous.revenue')('')}
                                    hasError={(touched.previous?.revenue && !!errors.previous?.revenue) as boolean}
                                    onBlur={handleBlur('previous.revenue')}
                                />
                            </div>
                            {/* Capital social */}
                            <div className={styles.inputGroup}>
                                <Input
                                    title="Capital social : N"
                                    name="current.capital"
                                    id="current.capital"
                                    width="100%"
                                    type="number"
                                    placeholder="Ex : 0"
                                    value={formatNumberWithSpaces(values.current.capital)}
                                    onChange={handleNumericChange('current.capital')}
                                    clearInput={() =>handleChange('current.capital')('')}
                                    hasError={(touched.current?.capital && !!errors.current?.capital) as boolean}
                                    onBlur={handleBlur('current.capital')}
                                />
                                <Input
                                    title="Capital social : N-1"
                                    name="previous.capital"
                                    id="previous.capital"
                                    width="100%"
                                    type="number"
                                    placeholder="Ex : 0"
                                    value={formatNumberWithSpaces(values.previous.capital)}
                                    onChange={handleNumericChange('previous.capital')}
                                    clearInput={() =>handleChange('previous.capital')('')}
                                    hasError={(touched.previous?.capital && !!errors.previous?.capital) as boolean}
                                    onBlur={handleBlur('previous.capital')}
                                />
                            </div>
                            {/* Capitaux propres */}
                            <div className={styles.inputGroup}>
                                <Input
                                    title="Capitaux propres : N"
                                    name="current.equity"
                                    id="current.equity"
                                    width="100%"
                                    type="number"
                                    placeholder="Ex : 0"
                                    value={formatNumberWithSpaces(values.current.equity)}
                                    onChange={handleNumericChange('current.equity')}
                                    clearInput={() =>handleChange('current.equity')('')}
                                    hasError={(touched.current?.equity && !!errors.current?.equity) as boolean}
                                    onBlur={handleBlur('current.equity')}
                                />
                                <Input
                                    title="Capitaux propres : N-1"
                                    name="previous.equity"
                                    id="previous.equity"
                                    width="100%"
                                    type="number"
                                    placeholder="Ex : 0"
                                    value={formatNumberWithSpaces(values.previous.equity)}
                                    onChange={handleNumericChange('previous.equity')}
                                    clearInput={() =>handleChange('previous.equity')('')}
                                    hasError={(touched.previous?.equity && !!errors.previous?.equity) as boolean}
                                    onBlur={handleBlur('previous.equity')}
                                />
                            </div>
                            {/* Total bilan */}
                            <div className={styles.inputGroup}>
                                <Input
                                    title="Total bilan : N"
                                    name="current.assets"
                                    id="current.assets"
                                    width="100%"
                                    type="number"
                                    placeholder="Ex : 0"
                                    value={formatNumberWithSpaces(values.current.assets)}
                                    onChange={handleNumericChange('current.assets')}
                                    clearInput={() =>handleChange('current.assets')('')}
                                    hasError={(touched.current?.assets && !!errors.current?.assets) as boolean}
                                    onBlur={handleBlur('current.assets')}
                                />
                                <Input
                                    title="Total bilan : N-1"
                                    name="previous.assets"
                                    id="previous.assets"
                                    width="100%"
                                    type="number"
                                    placeholder="Ex : 0"
                                    value={formatNumberWithSpaces(values.previous.assets)}
                                    onChange={handleNumericChange('previous.assets')}
                                    clearInput={() =>handleChange('previous.assets')('')}
                                    hasError={(touched.previous?.assets && !!errors.previous?.assets) as boolean}
                                    onBlur={handleBlur('previous.assets')}
                                />
                            </div>
                            {/* Disponibilités */}
                            <div className={styles.inputGroup}>
                                <Input
                                    title="Disponibilités (Trésorerie) : N"
                                    name="current.cash"
                                    id="current.cash"
                                    width="100%"
                                    type="number"
                                    placeholder="Ex : 0"
                                    value={formatNumberWithSpaces(values.current.cash)}
                                    onChange={handleNumericChange('current.cash')}
                                    clearInput={() =>handleChange('current.cash')('')}
                                    hasError={(touched.current?.cash && !!errors.current?.cash) as boolean}
                                    onBlur={handleBlur('current.cash')}
                                />
                                <Input
                                    title="Disponibilités (Trésorerie) : N-1"
                                    name="previous.cash"
                                    id="previous.cash"
                                    width="100%"
                                    type="number"
                                    placeholder="Ex : 0"
                                    value={formatNumberWithSpaces(values.previous.cash)}
                                    onChange={handleNumericChange('previous.cash')}
                                    clearInput={() =>handleChange('previous.cash')('')}
                                    hasError={(touched.previous?.cash && !!errors.previous?.cash) as boolean}
                                    onBlur={handleBlur('previous.cash')}
                                />
                            </div>
                            <ValidateButton title="Continuer" disabled={!isValid || isPending} onLoading={isPending} onClick={handleSubmit} />
                        </Form>
                    )
                }}
            </Formik>
        </Container>
    );
}
